"use client"

import { useState } from "react"
import { ExternalLink, FlaskConical, Link as LinkIcon, Megaphone, RefreshCw, Unplug } from "lucide-react"

import { IntegrationCardLayout } from "@/components/integrations/integration-card-layout"
import { MetaAdsLiveAdHandoff } from "@/components/integrations/meta-ads/meta-ads-live-ad-handoff"
import { MetaAdsSetupDialog } from "@/components/integrations/meta-ads/meta-ads-setup-dialog"
import { MetaAdsFollowUpDialog } from "@/components/integrations/meta-ads/meta-ads-follow-up-dialog"
import { MetaAdsTestDialog } from "@/components/integrations/meta-ads/meta-ads-test-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  useAcknowledgeMetaAdsFormHandoffMutation,
  useCheckMetaAdsMutation,
  useDisconnectMetaAdsMutation,
} from "@/hooks/mutations/meta-ads"
import { useMetaAdsIntegrationQuery } from "@/hooks/queries/meta-ads"
import {
  canSendMetaAdsCallbackTest,
  getLatestMetaAdsFormHandoffsBySource,
  isMetaAdsFormHandoffInScope,
} from "@/lib/meta-ads/readiness-flow"
import type {
  MetaAdsConnectionHealth,
  MetaAdsConnectionTest,
  MetaFollowUpAction,
} from "@/lib/meta-ads/types"

export function MetaAdsIntegrationCard({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const integrationQuery = useMetaAdsIntegrationQuery()
  const checkMutation = useCheckMetaAdsMutation()
  const acknowledgeHandoffMutation = useAcknowledgeMetaAdsFormHandoffMutation()
  const disconnectMutation = useDisconnectMetaAdsMutation()
  const [setupOpen, setSetupOpen] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  const [followUpOpen, setFollowUpOpen] = useState(false)
  const [handoffError, setHandoffError] = useState<string | null>(null)
  const integration = integrationQuery.data ?? null
  const isConfigured = integration && integration.status !== "disabled"
  const pendingHandoffs = integration
    ? getLatestMetaAdsFormHandoffsBySource(integration.form_handoffs).filter(handoff => (
        !handoff.acknowledgedAt
        && isMetaAdsFormHandoffInScope({
          handoff,
          formScope: integration.form_scope,
          selectedFormIds: integration.selected_form_ids,
        })
      ))
    : []

  async function acknowledgePendingHandoffs() {
    setHandoffError(null)
    try {
      for (const handoff of pendingHandoffs) {
        await acknowledgeHandoffMutation.mutateAsync(handoff.copiedForm.id)
      }
    } catch (error: unknown) {
      setHandoffError(error instanceof Error ? error.message : "The update could not be confirmed.")
    }
  }

  return (
    <>
      <IntegrationCardLayout
        title="Ads Platforms"
        description="Schedule follow-up calls, emails, or WhatsApp messages for Instant Form leads."
        icon={<Megaphone aria-hidden="true" className="h-5 w-5" />}
        defaultOpen={defaultOpen}
      >
        <div className="space-y-4">
          {integrationQuery.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted motion-reduce:animate-none" />
          ) : null}
          {integrationQuery.error ? (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>Meta Ads connection could not be loaded.</span>
                <Button size="sm" variant="outline" onClick={() => void integrationQuery.refetch()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          {!integrationQuery.isLoading && !integrationQuery.error && !isConfigured ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Connect Meta Ads, then choose whether an Instant Form lead gets a callback, email, or WhatsApp message first. You can add a different task if that first attempt fails.
              </p>
              <Button type="button" onClick={() => setSetupOpen(true)}>
                <LinkIcon aria-hidden="true" className="mr-2 h-4 w-4" />
                <span>Connect ads platform</span>
              </Button>
            </div>
          ) : null}

          {isConfigured ? (
            <div className="space-y-4 rounded-lg border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">Meta Ads</h3>
                    <StatusBadge health={integration.connection_health} />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {integration.meta_page_name} <span>· Page</span> {integration.meta_page_id}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
                    {integration.status === "draft" ? "Finish setup" : "Reconnect"}
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setFollowUpOpen(true)}>
                    <span>Choose first task and fallback</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={integration.connection_health === "verification_pending" || !canSendMetaAdsCallbackTest(integration)}
                    onClick={() => setTestOpen(true)}
                  >
                    <FlaskConical className="mr-2 h-4 w-4" />
                    <span>{integration.connection_test ? "Test again" : "Test connection"}</span>
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={checkMutation.isPending} onClick={() => checkMutation.mutate()}>
                    <RefreshCw className="mr-2 h-4 w-4" /> <span>Check connection</span>
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setDisconnectOpen(true)}>
                    <Unplug className="mr-2 h-4 w-4" /> <span>Disconnect</span>
                  </Button>
                </div>
              </div>

              <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
                <h4 className="font-medium">Lead task routing</h4>
                {Object.entries(integration.form_action_overrides).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(integration.form_action_overrides).map(([formId, actions]) => (
                      <p key={formId} className="break-words text-muted-foreground">
                        <span>Form {formId}: </span>
                        <span>{actions[0] ? actionLabel(actions[0].actionType) : "No follow-up"}</span>
                        {actions[1] ? <span> → if it fails, {actionLabel(actions[1].actionType)}</span> : null}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    {integration.voice_agent_id
                      ? "Forms currently use the legacy Voice callback. Choose a first task to change this."
                      : "No lead tasks configured yet. Choose a first task for each Instant Form before testing."}
                  </p>
                )}
              </div>

              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                {integration.voice_agent_id ? <Detail label="Legacy callback agent" value={integration.voice_agent_name ?? "Not selected"} /> : null}
                {integration.caller_phone_number ? <Detail label="Legacy caller number" value={integration.caller_phone_number} /> : null}
                <Detail
                  label="Meta ad account"
                  value={integration.meta_ad_account_name ?? "Choose during reconnect"}
                />
                <Detail
                  label="Lead forms"
                  value={integration.form_scope === "all"
                    ? "All Instant Forms"
                    : `${integration.selected_form_ids.length} selected`}
                />
                {integration.voice_agent_id ? <Detail label="Legacy callback hours" value={integration.respect_working_hours ? "Keep callbacks inside working hours" : "Call as soon as possible"} /> : null}
                <Detail label="Last connection check" value={formatDateTime(integration.last_checked_at)} />
                <Detail label="Last lead event" value={formatDateTime(integration.last_event_at)} />
                <Detail label="Last lead task" value={formatDateTime(integration.last_task_created_at)} />
              </dl>
              {!canSendMetaAdsCallbackTest(integration) ? <p className="text-sm text-muted-foreground">
                To test Email or WhatsApp follow-up, submit your own details through the live Instant Form after saving its tasks. The built-in test lead supplies only a phone number.
              </p> : null}

              {integration.last_error_code ? (
                <Alert variant="destructive">
                  <AlertDescription>{connectionMessage(integration.last_error_code)}</AlertDescription>
                </Alert>
              ) : null}
              {integration.connection_test ? (
                <ConnectionTestAlert test={integration.connection_test} />
              ) : null}
              {integration.recent_action_results.length > 0 ? (
                <div className="space-y-2 rounded-md border p-3 text-sm">
                  <h4 className="font-medium">Recent lead actions</h4>
                  {integration.recent_action_results.slice(0, 10).map(result => (
                    <p key={`${result.lead_id}:${result.action_stage}`} className="break-words text-muted-foreground">
                      <span>{result.action_stage} {result.action_type.replaceAll("_", " ")} · Lead {result.lead_id} · {result.status.replaceAll("_", " ")}</span>
                      {result.error_code ? <span> · {followUpErrorMessage(result.error_code)}</span> : null}
                    </p>
                  ))}
                </div>
              ) : null}
              {pendingHandoffs.length > 0 ? (
                <div className="space-y-3">
                  <MetaAdsLiveAdHandoff
                    handoffs={pendingHandoffs}
                    adAccountName={integration.meta_ad_account_name}
                  />
                  {handoffError ? <p className="text-sm text-destructive">{handoffError}</p> : null}
                  <div className="flex flex-wrap gap-2">
                    <Button asChild type="button" size="sm" variant="outline">
                      <a
                        href={integration.ads_manager_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span>Open Ads Manager</span>
                        <ExternalLink aria-hidden="true" className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={acknowledgeHandoffMutation.isPending}
                      onClick={() => void acknowledgePendingHandoffs()}
                    >
                      {acknowledgeHandoffMutation.isPending
                        ? "Saving…"
                        : `I Updated My Ad${pendingHandoffs.length === 1 ? "" : "s"}`}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </IntegrationCardLayout>

      <MetaAdsSetupDialog
        open={setupOpen}
        integration={isConfigured ? integration : null}
        onOpenChange={setSetupOpen}
      />
      {isConfigured ? <MetaAdsFollowUpDialog open={followUpOpen} onOpenChange={setFollowUpOpen} integration={integration} /> : null}
      <MetaAdsTestDialog open={testOpen} onOpenChange={setTestOpen} />
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect and delete Meta Ads data?</AlertDialogTitle>
            <AlertDialogDescription>
              All Meta Ads integration data stored in Versafy will be permanently deleted, including the connection, lead history and source details, form setup records, and every Agent Task created from Meta Ads with its activity history. This cannot be undone. Your campaigns, ads, and forms in Meta are not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep connected</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={disconnectMutation.isPending}
              onClick={event => {
                event.preventDefault()
                disconnectMutation.mutate(undefined, {
                  onSuccess: () => setDisconnectOpen(false),
                })
              }}
            >
              {disconnectMutation.isPending ? "Deleting…" : "Disconnect and delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function actionLabel(actionType: MetaFollowUpAction["actionType"]): string {
  switch (actionType) {
    case "schedule_call": return "Schedule Call"
    case "send_email": return "Send Email"
    case "send_whatsapp": return "Send WhatsApp"
  }
}

function followUpErrorMessage(code: string): string {
  switch (code) {
    case "form_disclosure_changed": return "Review the form contact disclosure"
    case "contact_field_missing": return "A mapped form field is missing"
    case "lead_email_invalid": return "Lead email is invalid"
    case "lead_phone_invalid": return "Lead phone number is invalid"
    case "whatsapp_consent_unavailable": return "WhatsApp consent is unavailable or withdrawn"
    case "whatsapp_template_unavailable": return "WhatsApp template is unavailable"
    case "agent_or_sender_unavailable": return "Agent or sending account is unavailable"
    case "primary_blocked_before_transport": return "Primary action stopped before sending"
    case "voice_no_answer": return "Primary call was not answered"
    case "voice_busy": return "Primary call reached a busy line"
    case "voice_voicemail": return "Primary call reached voicemail"
    case "voice_provider_failure": return "Primary call failed at the provider"
    default: return code.replaceAll("_", " ")
  }
}

function StatusBadge({ health }: { health: MetaAdsConnectionHealth }) {
  switch (health) {
    case "connected_tested":
      return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Connected and tested</Badge>
    case "connected_untested":
      return <Badge variant="secondary">Connected, not tested</Badge>
    case "verification_pending":
      return <Badge variant="secondary">Test in progress</Badge>
    case "action_required_meta":
      return <Badge variant="destructive">Action required in Meta</Badge>
    case "authorization_expired":
      return <Badge variant="destructive">Authorization expired</Badge>
    case "needs_attention":
      return <Badge variant="destructive">Needs attention</Badge>
    default: {
      const exhaustiveHealth: never = health
      return exhaustiveHealth
    }
  }
}

function ConnectionTestAlert({ test }: { test: MetaAdsConnectionTest }) {
  switch (test.status) {
    case "submitted":
      return (
        <Alert>
          <AlertDescription>Test lead sent to Meta. Waiting for the webhook…</AlertDescription>
        </Alert>
      )
    case "webhook_received":
      return (
        <Alert>
          <AlertDescription>Webhook received. Retrieving the test lead…</AlertDescription>
        </Alert>
      )
    case "lead_retrieved":
      return (
        <Alert>
          <AlertDescription>Test lead retrieved. Creating its callback task…</AlertDescription>
        </Alert>
      )
    case "task_created":
      return (
        <Alert>
          <AlertDescription>
            <span>Test passed. The callback task was created</span>
            <span>{test.scheduled_for ? ` for ${new Date(test.scheduled_for).toLocaleString()}` : ""}</span><span>.</span>
          </AlertDescription>
        </Alert>
      )
    case "timed_out":
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Meta did not deliver the test lead. Ask a business admin to assign Versafy under
            Business Settings → Integrations → Leads Access → CRMs, then test again.
          </AlertDescription>
        </Alert>
      )
    case "failed":
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Meta delivered the test lead, but Versafy could not create its callback task. Check
            the form phone field and connection, then test again.
          </AlertDescription>
        </Alert>
      )
    default: {
      const exhaustiveStatus: never = test.status
      return exhaustiveStatus
    }
  }
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "No events received yet"
}

function connectionMessage(code: string): string {
  switch (code) {
    case "leadgen_not_subscribed":
      return "Versafy is no longer subscribed to lead events for this Page. Reconnect Meta Ads."
    case "missing_permissions":
      return "The Meta connection no longer grants the required Business Portfolio, Page, and lead access. Reconnect Meta Ads."
    case "connection_check_failed":
      return "The connection could not be checked. Reconnect Meta Ads or try again."
    case "meta_lead_access_required":
      return "A Meta business admin must approve Versafy in Business Settings → Integrations → Leads Access → CRMs, then check the connection again."
    case "meta_lead_access_unverified":
      return "Meta lead retrieval could not be verified due to a temporary error. Check the connection again."
    case "form_phone_field_missing":
      return "At least one selected Instant Form is missing the configured phone field. Reconnect to review forms or create a compatible copy."
    case "no_active_instant_forms":
      return "The connected Page has no active Instant Forms. Publish a form in Meta, then check the connection again."
    case "authorization_expired":
    case "credentials_missing":
      return "The Meta authorization has expired. Reconnect Meta Ads."
    default:
      return "Meta Ads needs attention. Reconnect the Facebook Page and try again."
  }
}
